import React, { useId, useRef, useState } from 'react';
import { Button } from '@components/ui/button';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';

interface PinEntryRowProps {
  /** Screen-reader label of the input, e.g. "{Space} PIN". Not shown. */
  label: string;
  placeholder: string;
  submitLabel: string;
  submittingLabel: string;
  isSubmitting: boolean;
  error: string | null;
  /** When this changes the input plays a short horizontal shake (not under reduced motion). */
  shakeKey?: number;
  onSubmit: (pin: string) => void;
  autoFocus?: boolean;
  /** Classes of the submit button; defaults to the Space PIN drawer's look. */
  submitClassName?: string;
}

/** The Space PIN drawer's Join. Its focus ring is mb-teal-ink with an offset, as every Button's is;
 *  without the offset it would sit on the teal fill at 2.52:1 (ui-ux-designer, Focus States). */
const DEFAULT_SUBMIT_CLASS =
  'bg-mb-teal hover:bg-mb-teal-dark active:bg-mb-teal-dark text-mb-ink rounded-full h-12 text-base font-medium w-full focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2';

/**
 * The PIN input, its error line and the submit button, shared by the Space PIN drawer and the
 * Going PIN sheet. It owns the typed PIN: remount it (change its `key`) to clear it.
 *
 * While a PIN is checked the input stays focusable (read-only and `aria-busy`, never disabled): a
 * disabled input blurs, so focus would fall to the body, the mobile keyboard would close after
 * every wrong PIN and a screen reader would lose its place. `autoComplete="off"`: a password
 * manager neither offers to save the PIN nor fills it.
 */
export const PinEntryRow: React.FC<PinEntryRowProps> = ({
  label,
  placeholder,
  submitLabel,
  submittingLabel,
  isSubmitting,
  error,
  shakeKey = 0,
  onSubmit,
  autoFocus,
  submitClassName = DEFAULT_SUBMIT_CLASS,
}) => {
  const [pin, setPin] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  // The error line describes the field, so re-reading it (focus stays there) says why again.
  const errorId = useId();
  const reducedMotion = usePrefersReducedMotion();
  // Shake on a shakeKey change: the class is removed when the animation ends, so the next change
  // replays it without remounting the input (which would drop focus and close the keyboard).
  const [seenShakeKey, setSeenShakeKey] = useState(shakeKey);
  const [shaking, setShaking] = useState(false);
  if (seenShakeKey !== shakeKey) {
    setSeenShakeKey(shakeKey);
    setShaking(true);
  }

  const handleSubmit = () => {
    if (isSubmitting || !pin.trim()) return;
    // Synchronously, inside the tap, before the button disables: the keyboard stays open.
    inputRef.current?.focus();
    onSubmit(pin.trim());
  };

  return (
    // data-pin-entry: the row the Going PIN sheet keeps in view while the keyboard is up.
    <div data-pin-entry="" className="flex flex-col gap-3">
      <div
        data-testid="pin-shake"
        className={shaking && !reducedMotion ? 'animate-pin-shake' : undefined}
        onAnimationEnd={() => {
          setShaking(false);
        }}
      >
        {/* Boundary in mb-control-border and focus in mb-teal-ink: both clear the 3:1 a
            non-text boundary and a focus indicator need here (ui-ux-designer, WCAG 1.4.11).
            rounded-xl: the scale radius the app's inputs use, 2px from the 14px it replaces.
            focus:outline-hidden, scoped: Tailwind's outline-hidden draws a 2px outline in
            forced-colors mode, which unscoped painted the field at rest, not on focus. */}
        <input
          type="password"
          name="pin"
          inputMode="numeric"
          enterKeyHint="go"
          pattern="[0-9]*"
          aria-label={label}
          value={pin}
          onChange={e => {
            setPin(e.target.value);
          }}
          onKeyDown={e => {
            // One send per press: a held Enter repeats into this field, which keeps focus through
            // the check, and each repeat would spend another of the limited PIN attempts.
            if (e.key !== 'Enter' || e.repeat) return;
            // The Enter that confirms an IME candidate is not a submit. Safari reports it with
            // isComposing already false, so keyCode 229 is the part that catches it there.
            // eslint-disable-next-line @typescript-eslint/no-deprecated -- no standard field marks Safari's IME Enter
            if (e.nativeEvent.isComposing || e.keyCode === 229) return;
            handleSubmit();
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          autoComplete="off"
          className="w-full h-12 text-center text-lg bg-mb-offwhite border border-mb-control-border rounded-xl focus:outline-hidden focus:border-mb-teal-ink focus:ring-1 focus:ring-mb-teal-ink"
          ref={inputRef}
          readOnly={isSubmitting}
          aria-busy={isSubmitting}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
        />
      </div>

      {error ? (
        <p id={errorId} role="alert" className="text-sm text-destructive text-center">
          {error}
        </p>
      ) : null}

      <Button
        onClick={handleSubmit}
        disabled={isSubmitting || !pin.trim()}
        className={submitClassName}
      >
        {isSubmitting ? submittingLabel : submitLabel}
      </Button>
    </div>
  );
};
